import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Info } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useI18n } from "../i18n/LanguageContext";
import { useSeo } from "../hooks/useSeo";

function PriceTable({ rows, testid }) {
  const { t } = useI18n();
  return (
    <div className="rounded-md border border-border overflow-hidden divide-y divide-border" data-testid={testid}>
      {rows.map((r, i) => (
        <div key={i} className="grid md:grid-cols-12 gap-4 p-6 md:p-7 items-start bg-card hover:bg-secondary/30 transition-colors">
          <div className="md:col-span-6">
            <p className="text-lg text-foreground font-medium">{r.name}</p>
            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{r.note}</p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <p className="font-display text-2xl md:text-3xl text-primary">{r.price}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t.common.excl_vat}</p>
          </div>
          <div className="md:col-span-2 md:text-right">
            <Link to="/contact?intent=intake" className="inline-flex items-center gap-1.5 text-sm text-accent link-underline">
              {t.common.contact_us} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Tarieven() {
  const { t, lang } = useI18n();
  useSeo({ title: t.seo.pricing.title, description: t.seo.pricing.description, path: "/tarieven", lang });
  const [tab, setTab] = useState("expat");
  return (
    <section className="container-wide py-16 md:py-24">
      <div className="max-w-3xl">
        <p className="eyebrow">{t.pricing.eyebrow}</p>
        <h1 className="mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium text-foreground">{t.pricing.title}</h1>
        <p className="mt-5 text-lg text-muted-foreground leading-relaxed">{t.pricing.lead}</p>
      </div>

      <div className="mt-12">
        <Tabs value={tab} onValueChange={setTab} className="w-full" data-testid="pricing-tabs">
          <TabsList className="bg-transparent p-0 h-auto gap-2 border-b border-border w-full justify-start rounded-none">
            <TabsTrigger
              value="expat"
              data-testid="tab-expat"
              className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-accent data-[state=active]:border-accent border-b-2 border-transparent px-4 py-3 -mb-px text-sm tracking-tight"
            >
              {t.pricing.tabExpat}
            </TabsTrigger>
            <TabsTrigger
              value="business"
              data-testid="tab-business"
              className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-accent data-[state=active]:border-accent border-b-2 border-transparent px-4 py-3 -mb-px text-sm tracking-tight"
            >
              {t.pricing.tabBusiness}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="expat" className="mt-8">
            <PriceTable rows={t.pricing.expat} testid="pricing-table-expat" />
          </TabsContent>
          <TabsContent value="business" className="mt-8">
            <PriceTable rows={t.pricing.business} testid="pricing-table-business" />
          </TabsContent>
        </Tabs>
      </div>

      <div className="mt-12 grid md:grid-cols-12 gap-5">
        <div className="md:col-span-7 card-soft p-7 flex items-start gap-4" data-testid="regional-note">
          <span className="h-9 w-9 rounded-full bg-accent/15 grid place-items-center text-accent flex-shrink-0">
            <Info className="h-4 w-4" />
          </span>
          <div>
            <h3 className="text-lg text-foreground font-medium">{t.pricing.regionalTitle}</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t.pricing.regionalBody}</p>
          </div>
        </div>
        <div className="md:col-span-5 card-soft p-7 flex flex-col justify-between">
          <p className="text-muted-foreground leading-relaxed">{t.home.ctaSub}</p>
          <Link to="/contact?intent=intake" className="btn-primary mt-6 self-start" data-testid="pricing-cta">
            {t.nav.bookIntake} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
